// frontend/src/components/ImageGenerationHub/ImageGenerationHub.js
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  TextField,
  CircularProgress,
  Link as MuiLink,
  Tooltip,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Chip,
  CardMedia,
  Divider
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ClearIcon from '@mui/icons-material/Clear';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import CommodityAutocomplete from '../Common/CommodityAutocomplete';
import apiClient from '../../api/client';

import { checkDuplicateGtins, createBatchImagesAsync } from '../../api/images';

const logger = console;

const ImageGenerationHub = () => {
  const [gtinInput, setGtinInput] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [commodity, setCommodity] = useState('');
  const [subCommodity, setSubCommodity] = useState('');

  // Duplicate confirmation dialog state
  const [dupDialogOpen, setDupDialogOpen] = useState(false);
  const [dupDialogData, setDupDialogData] = useState(null); // { duplicates, allGtins, nonDupGtins }
  const [excludedGtins, setExcludedGtins] = useState(new Set());

  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.name.endsWith('.txt') || file.name.endsWith('.csv')) {
        setUploadedFile(file);
        setGtinInput('');
        setStatusMessage({ type: 'info', message: `File selected: ${file.name}` });
      } else {
        setStatusMessage({ type: 'error', message: 'Invalid file type. Please upload a .txt or .csv file.' });
        setUploadedFile(null);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/plain': ['.txt'], 'text/csv': ['.csv'] },
    multiple: false,
  });

  const handleGtinInputChange = (event) => {
    setGtinInput(event.target.value);
    if (event.target.value && uploadedFile) {
      setUploadedFile(null);
      setStatusMessage(null);
    }
  };

  const clearInputs = () => {
    setGtinInput('');
    setUploadedFile(null);
    setStatusMessage(null);
    setCommodity('');
    setSubCommodity('');
  };

  // Extract GTINs from current input
  const getGtinsFromInput = async () => {
    if (uploadedFile) {
      const fileContent = await uploadedFile.text();
      return fileContent.split('\n').map(g => g.trim()).filter(g => g);
    } else if (gtinInput.trim()) {
      return gtinInput.split('\n').map(g => g.trim()).filter(g => g);
    }
    return [];
  };

  // Submit GTINs for generation (called after duplicate check passes or user confirms)
  const submitGtins = async (gtins) => {
    setProcessing(true);
    setStatusMessage(null);
    try {
      const response = await createBatchImagesAsync({
        gtins,
        commodity: commodity.trim() || undefined,
        sub_commodity: subCommodity.trim() || undefined
      });
      const gtinCount = response.image_details?.length || 0;
      setStatusMessage({
        type: 'success',
        message: `${gtinCount} GTIN(s) submitted for processing.`
      });
      setGtinInput('');
      setUploadedFile(null);
    } catch (error) {
      logger.error('Error submitting GTINs for generation:', error);
      setStatusMessage({ type: 'error', message: error.message || 'An error occurred during submission.' });
    } finally {
      setProcessing(false);
    }
  };

  // Main submit handler - checks for duplicates first
  const handleSubmit = async () => {
    setStatusMessage(null);

    const gtins = await getGtinsFromInput();
    if (gtins.length === 0) {
      setStatusMessage({ type: 'error', message: 'No valid GTINs found.' });
      return;
    }

    // Check for duplicates before generating
    setChecking(true);
    try {
      const result = await checkDuplicateGtins(gtins);
      const duplicates = result.duplicates || [];

      if (duplicates.length > 0) {
        // Group duplicates by GTIN
        const dupGtins = new Set(duplicates.map(d => d.gtin));
        const nonDupGtins = gtins.filter(g => !dupGtins.has(g));

        setDupDialogData({ duplicates, allGtins: gtins, nonDupGtins, dupGtins: [...dupGtins] });
        setExcludedGtins(new Set(dupGtins)); // Default: exclude duplicates
        setDupDialogOpen(true);
      } else {
        // No duplicates, proceed directly
        await submitGtins(gtins);
      }
    } catch (error) {
      logger.warn('Duplicate check failed, proceeding anyway:', error);
      await submitGtins(gtins);
    } finally {
      setChecking(false);
    }
  };

  // Handle dialog confirmation
  const handleDupDialogConfirm = async () => {
    if (!dupDialogData) return;

    const gtinsToGenerate = dupDialogData.allGtins.filter(g => !excludedGtins.has(g));
    setDupDialogOpen(false);
    setDupDialogData(null);

    if (gtinsToGenerate.length === 0) {
      setStatusMessage({ type: 'info', message: 'No GTINs selected for generation.' });
      return;
    }

    await submitGtins(gtinsToGenerate);
  };

  const toggleGtinExclusion = (gtin) => {
    setExcludedGtins(prev => {
      const next = new Set(prev);
      if (next.has(gtin)) {
        next.delete(gtin);
      } else {
        next.add(gtin);
      }
      return next;
    });
  };

  const isProcessing = processing || checking;

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, mt: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
          Generate MRHIs
        </Typography>
        <Typography variant="body2" align="center" sx={{ mb: 1 }}>
          This app will generate an image based on UPCs or GTINs, no need to upload images.
        </Typography>
        <Typography variant="subtitle1" align="center" color="textSecondary" sx={{ mb: 3 }}>
          Enter GTINs directly (one per line) or upload a file.
        </Typography>

        {statusMessage && (
          <Alert severity={statusMessage.type} sx={{ mb: 2 }} onClose={() => setStatusMessage(null)}>
            {statusMessage.message}
            {statusMessage.type === 'success' && (
              <MuiLink component={RouterLink} to="/manage-images" sx={{ ml: 1, fontWeight: 'bold' }}>
                View Progress
              </MuiLink>
            )}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom color="textSecondary">
            Optional: Assign commodity numbers to all images
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <CommodityAutocomplete
                value={commodity}
                onChange={setCommodity}
                disabled={isProcessing}
                label="Commodity"
                placeholder="Type number or name..."
              />
            </Box>
            <TextField
              label="Sub-Commodity"
              value={subCommodity}
              onChange={(e) => setSubCommodity(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ flex: 1 }}
              placeholder="e.g., 456"
              disabled={isProcessing}
            />
          </Box>
        </Box>

        <TextField
          label="Enter GTINs (one per line)"
          multiline
          rows={8}
          value={gtinInput}
          onChange={handleGtinInputChange}
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          placeholder="00012345678900&#10;00012345678901&#10;..."
          disabled={isProcessing}
        />

        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            mb: 2,
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper'
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          {uploadedFile ? (
            <Typography>{uploadedFile.name}</Typography>
          ) : isDragActive ? (
            <Typography>Drop the file here ...</Typography>
          ) : (
            <Typography>Drag 'n' drop a .txt or .csv file here, or click to select file</Typography>
          )}
        </Box>

        {uploadedFile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>Selected file: <strong>{uploadedFile.name}</strong></Typography>
            <Tooltip title="Clear selected file">
              <IconButton onClick={() => { setUploadedFile(null); setStatusMessage(null); }} size="small" disabled={isProcessing}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button
            onClick={clearInputs}
            variant="outlined"
            disabled={isProcessing || (!gtinInput && !uploadedFile)}
          >
            Clear
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            size="large"
            disabled={isProcessing || (!gtinInput.trim() && !uploadedFile)}
            startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          >
            {checking ? 'Checking...' : processing ? 'Processing...' : 'Generate MRHIs'}
          </Button>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            component={RouterLink}
            to="/manage-images"
            variant="text"
            startIcon={<ListAltIcon />}
          >
            Go to Review & Manage Page
          </Button>
        </Box>
      </Paper>

      {/* Duplicate Confirmation Dialog */}
      <Dialog open={dupDialogOpen} onClose={() => setDupDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningAmberIcon color="warning" />
            <Typography variant="h6">Existing MRHIs Found</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {dupDialogData && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {dupDialogData.dupGtins.length} of your {dupDialogData.allGtins.length} GTIN(s) already have MRHIs created by other users.
                Uncheck any you still want to generate. Checked items will be skipped.
              </Alert>

              <List sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {dupDialogData.dupGtins.map((gtin, idx) => {
                  const dupsForGtin = dupDialogData.duplicates.filter(d => d.gtin === gtin);
                  const isExcluded = excludedGtins.has(gtin);

                  return (
                    <React.Fragment key={gtin}>
                      {idx > 0 && <Divider />}
                      <ListItem
                        sx={{
                          alignItems: 'flex-start',
                          bgcolor: isExcluded ? 'action.hover' : 'transparent',
                          opacity: isExcluded ? 0.7 : 1
                        }}
                      >
                        <Checkbox
                          checked={isExcluded}
                          onChange={() => toggleGtinExclusion(gtin)}
                          sx={{ mt: 0.5 }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                          <CardMedia
                            component="img"
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: 'contain',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'grey.50'
                            }}
                            image={`${apiClient.defaults.baseURL}/images/${dupsForGtin[0].existing_image_id}/thumbnail?size=200`}
                            alt={`Existing MRHI for ${gtin}`}
                            onError={(e) => { e.target.src = 'https://placehold.co/200x200/eee/ccc?text=No+Preview'; }}
                          />
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">GTIN: {gtin}</Typography>
                                {isExcluded && <Chip label="Will skip" size="small" color="default" />}
                                {!isExcluded && <Chip label="Will generate" size="small" color="primary" />}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                {dupsForGtin.map((dup, i) => (
                                  <Typography key={i} variant="caption" display="block">
                                    Existing: {dup.owner_username || dup.owner_email} &mdash; {dup.status}
                                    {dup.is_submitted && <Chip label="Submitted" size="small" color="success" variant="outlined" sx={{ ml: 1, height: 18 }} />}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          />
                        </Box>
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>

              {dupDialogData.nonDupGtins.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {dupDialogData.nonDupGtins.length} other GTIN(s) have no duplicates and will be generated.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDupDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setExcludedGtins(new Set());
              }}
            >
              Generate All
            </Button>
            <Button
              variant="contained"
              onClick={handleDupDialogConfirm}
              disabled={dupDialogData && dupDialogData.allGtins.filter(g => !excludedGtins.has(g)).length === 0}
            >
              Confirm ({dupDialogData ? dupDialogData.allGtins.filter(g => !excludedGtins.has(g)).length : 0} GTINs)
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ImageGenerationHub;
